import { useEffect, useState } from 'preact/hooks'
import { useLocation } from 'wouter-preact'
import { Button } from '~/components/primitives/Button'
import type { GeminiFailure } from '~/contracts/gemini/errors'
import type { GeminiRequestPayload } from '~/contracts/gemini/request'
import type { GeminiProposal, RefusedRule } from '~/contracts/gemini/response'
import { REFUSED_RULE_COPY } from '~/contracts/gemini/response'
import { useAsyncData } from '~/hooks/useAsyncData'
import { planFromProposalPath } from '~/routes'
import { type ReviewOutcome, requestReview, reviewAvailability } from '~/services/aiReviewService'
import { buildGeminiPayload } from '~/services/geminiPayload'
import { collectPayloadSources } from '~/services/geminiPayloadSources'
import { useDatabase } from '~/storage/DatabaseContext'
import { addAiProposal, listAiProposals } from '~/storage/repositories/aiProposalRepository'
import { STORE } from '~/storage/schema'
import { calendarDateFrom, toIsoTimestamp } from '~/types/dates'
import { formatCalendarDate } from '~/utils/format'
import styles from './SecondOpinion.module.css'

/**
 * Asking someone else what they think of a plan Sola has already made.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  SOLA'S OPINION COMES FIRST, STRUCTURALLY
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  This renders **below** the decision panel on the Weekly Review screen, and
 *  that placement is the mechanism rather than a layout preference (D-008,
 *  D-093). The app's own answer is on screen before the offer to ask anyone
 *  else, so a second opinion is always second. Put on My Plan or in Settings it
 *  would be reachable without having read Sola's answer, and "AI is permanently
 *  a second opinion" would become a convention instead of a structure.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THE PREVIEW IS MANDATORY AND CANNOT BE TURNED OFF
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  Every request shows what will be sent and what will not, and requires an
 *  explicit action afterwards (D-093 §2). **There is no permanent consent flag**
 *  — no "don't ask again", no remembered choice — because the thing being
 *  consented to changes every week. The measurements option is off by default
 *  and is chosen per request; it is never remembered.
 *
 *  The preview describes the payload in the user's words rather than dumping
 *  JSON. A field list is not informed consent for someone who does not read
 *  JSON, and the point is that they understand it, not that they were shown it.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  TWELVE FAILURE STATES, AND NONE OF THEM SAYS "SOMETHING WENT WRONG"
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  `featureDisabled` renders as a clean state and never as an outage (D-095 §4):
 *  the app must not say "unavailable" when the truth is that its owner switched
 *  it off, or that this build has no Worker. A refusal names the rule that was
 *  broken (D-094 §3) — *"the reply named a calorie target, which Project Sola
 *  never shows"* is the difference between an app that looks broken and one that
 *  looks principled — and the raw text is never shown or stored.
 */

type Stage =
  | { readonly kind: 'idle' }
  | { readonly kind: 'preview'; readonly payload: GeminiRequestPayload }
  | { readonly kind: 'sending' }
  | {
      readonly kind: 'done'
      readonly outcome: ReviewOutcome
      /**
       * The id the proposal was stored under, when it was.
       *
       * Absent when the reply was refused, and absent when the write itself
       * failed — and in the second case the proposal is still shown. **A pre-fill
       * link is what is missing, not the answer**: the accept path re-reads the
       * record by id (D-093 §6), so offering the link without one would send the
       * user to an editor with nothing in it.
       */
      readonly storedId?: string
    }

/**
 * What each failure means, in words a person can act on.
 *
 * One entry per category, exhaustively — `Record<GeminiFailure, …>` means a
 * thirteenth category cannot be added without a state here, which is the
 * client-side half of keeping `GEMINI_FAILURES` a closed, counted list.
 */
const FAILURE_COPY: Record<GeminiFailure, string> = {
  featureDisabled:
    'A second opinion is not switched on for this app right now. Everything else works normally.',
  authenticationRefused:
    'This device is not connected to your review service. Add or replace the access token in Settings → Connections.',
  rateLimitReached: 'You have asked for a second opinion a few times today. Try again tomorrow.',
  monthlyCeilingReached:
    'The monthly limit for second opinions has been reached. It resets at the start of next month.',
  invalidSolaPayload:
    'Project Sola could not put this week into a shape it is willing to send, so nothing was sent.',
  unsupportedContractVersion:
    'This version of Project Sola and your review service do not agree on the format. Updating the app should fix it.',
  geminiUnavailable:
    'The reviewer could not be reached. Your own recommendation above is unaffected.',
  geminiRateLimited: 'The reviewer is busy right now. Trying again in a few minutes usually works.',
  geminiSafetyRefusal:
    'The reviewer declined to answer this one. Asking again will not change that.',
  invalidGeminiResponse:
    'The reply did not arrive in a form Project Sola could read, so it was discarded.',
  requestTimedOut: 'The reviewer took too long to answer. Nothing was changed.',
  responseRefusedBySola: 'Project Sola refused the reply because it broke one of its own rules.',
}

export function SecondOpinion() {
  const db = useDatabase()
  const [stage, setStage] = useState<Stage>({ kind: 'idle' })
  const [includeMeasurements, setIncludeMeasurements] = useState(false)

  const { state, reload } = useAsyncData(
    async () => reviewAvailability(db),
    /* Re-read when the token changes, so saving one in Settings makes the entry
       point live without a reload. */
    [STORE.connections],
  )

  /**
   * Re-check when the browser changes its mind about the network.
   *
   * Without this the availability answer is whatever it was at mount, so someone
   * who loses signal after opening the screen keeps an enabled button that fails
   * when tapped — an outcome D-093 §4 specifically rejects, since the control is
   * supposed to be visibly disabled rather than disappointing.
   *
   * **`navigator.onLine` is a weak signal and is treated as one.** It reports
   * whether a network interface exists, not whether anything is reachable, so it
   * is right when it says `false` and merely optimistic when it says `true`. It
   * is a cheap way to avoid a doomed request, never the guarantee — the real
   * guarantee is that an unreachable network produces `geminiUnavailable` with
   * nothing queued, which is what the browser test asserts, because Playwright's
   * `setOffline` does not move this flag at all.
   */
  useEffect(() => {
    const recheck = () => reload()
    globalThis.addEventListener('online', recheck)
    globalThis.addEventListener('offline', recheck)
    return () => {
      globalThis.removeEventListener('online', recheck)
      globalThis.removeEventListener('offline', recheck)
    }
  }, [reload])

  if (state.status !== 'ready') return null

  const availability = state.value

  const openPreview = async () => {
    const sources = await collectPayloadSources(db, new Date())
    if (!sources.ok) {
      setStage({
        kind: 'done',
        outcome: { ok: false, failure: 'invalidSolaPayload', retryable: false },
      })
      return
    }

    setStage({ kind: 'preview', payload: buildGeminiPayload(sources.value) })
  }

  const send = async (payload: GeminiRequestPayload) => {
    setStage({ kind: 'sending' })
    const outcome = await requestReview(db, payload)

    /* Stored on the way to the screen, not after the user dismisses it (D-094
       §5). Navigating away mid-read must not lose the record — discarding on
       navigation is precisely what phase 3 did and phase 4 exists to stop.

       **Only a clean proposal is stored.** A refusal is not: D-094 §3 discards
       the whole response and never shows or stores the raw text, so there is
       nothing here worth keeping and keeping it would defeat the gate.

       **A failed write does not change what the user sees.** They asked, the
       reviewer answered, and the answer is on screen; turning a storage problem
       into a failure state would hide a real reply behind a bookkeeping error.
       The proposal is shown either way. */
    let storedId: string | undefined
    if (outcome.ok) {
      const stored = await addAiProposal(
        db,
        {
          proposal: outcome.proposal,
          disclosureProfile: payload.disclosureProfile,
          requestedAt: toIsoTimestamp(new Date()),
        },
        new Date(),
      )
      if (stored.ok) storedId = stored.value.id
    }

    setStage({ kind: 'done', outcome, ...(storedId === undefined ? {} : { storedId }) })
    /* Reset per request (D-093 §2). The choice is never remembered, so the next
       preview starts from the default again. */
    setIncludeMeasurements(false)
  }

  return (
    <section class={styles.section} aria-labelledby="second-opinion-heading">
      <h2 id="second-opinion-heading" class={styles.heading}>
        Want a second opinion?
      </h2>

      <p class={styles.lede}>
        Gemini reviews the plan Project Sola has already made. It never changes your route, your
        phase or your targets — it comments on them.
      </p>

      {availability.available ? null : (
        <p class={styles.unavailable}>{FAILURE_COPY[availability.failure]}</p>
      )}

      {stage.kind === 'idle' ? (
        <Button onClick={openPreview} disabled={!availability.available}>
          See what would be sent
        </Button>
      ) : null}

      {stage.kind === 'preview' ? (
        <Preview
          payload={stage.payload}
          includeMeasurements={includeMeasurements}
          onToggleMeasurements={setIncludeMeasurements}
          onCancel={() => setStage({ kind: 'idle' })}
          onSend={send}
        />
      ) : null}

      {stage.kind === 'sending' ? <p class={styles.sending}>Asking…</p> : null}

      {stage.kind === 'done' ? (
        <Result
          outcome={stage.outcome}
          storedId={stage.storedId}
          onDismiss={() => setStage({ kind: 'idle' })}
        />
      ) : null}

      <EarlierOpinions />
    </section>
  )
}

/**
 * What will be sent, and what will not.
 *
 * **The absences are listed as prominently as the inclusions**, because the
 * question someone actually has is "does this send my photos" and an inclusion
 * list answers it only by omission. Naming them explicitly is what makes the
 * preview informed rather than merely present.
 */
function Preview({
  payload,
  includeMeasurements,
  onToggleMeasurements,
  onCancel,
  onSend,
}: {
  payload: GeminiRequestPayload
  includeMeasurements: boolean
  onToggleMeasurements: (next: boolean) => void
  onCancel: () => void
  onSend: (payload: GeminiRequestPayload) => void
}) {
  const domains = payload.domains.map((domain) => domain.domain).join(', ')

  return (
    <div class={styles.preview}>
      <h3 class={styles.previewHeading}>What gets sent</h3>
      <ul class={styles.list}>
        <li>Which parts of your plan are active{domains === '' ? '' : ` — ${domains}`}</li>
        {payload.phase === undefined ? null : (
          <li>Your current phase, which week of it you are in, and the rate you are aiming for</li>
        )}
        <li>Which way your weight and waist are moving, as directions and percentages</li>
        <li>How closely you followed the plan, and how you have been recovering</li>
        <li>Which evidence is complete and which is missing</li>
      </ul>

      <h3 class={styles.previewHeading}>What never gets sent</h3>
      <ul class={styles.list}>
        <li>Your photos</li>
        <li>Anything you have written — notes, your project name, reasons</li>
        <li>Your date of birth, and any calendar date at all</li>
        <li>Individual weigh-ins, measurements or daily answers</li>
      </ul>

      <label class={styles.option}>
        <input
          type="checkbox"
          checked={includeMeasurements}
          onChange={(event) =>
            onToggleMeasurements((event.currentTarget as HTMLInputElement).checked)
          }
        />
        <span>
          Also include my current weight, waist, height and estimated body fat
          <span class={styles.optionHint}>Off unless you choose it, every time you ask.</span>
        </span>
      </label>

      <div class={styles.actions}>
        <Button
          onClick={() =>
            onSend(
              includeMeasurements
                ? payload
                : /* The measurements block is rebuilt rather than toggled on a
                     stored payload, so an unchecked box cannot leave a stale
                     block behind. Today the builder only adds one when asked,
                     so this is belt and braces on the field that matters most. */
                  { ...payload, disclosureProfile: 'default' as const },
            )
          }
        >
          Send this and ask
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          Not now
        </Button>
      </div>
    </div>
  )
}

function Result({
  outcome,
  storedId,
  onDismiss,
}: {
  outcome: ReviewOutcome
  storedId: string | undefined
  onDismiss: () => void
}) {
  if (outcome.ok) {
    return <Proposal proposal={outcome.proposal} storedId={storedId} onDismiss={onDismiss} />
  }

  return (
    <div class={styles.failure} role="status">
      <p class={styles.failureText}>{FAILURE_COPY[outcome.failure]}</p>
      {outcome.refusedRule === undefined ? null : (
        <p class={styles.refusedRule}>{ruleCopy(outcome.refusedRule)}</p>
      )}
      <div class={styles.actions}>
        <Button variant="secondary" onClick={onDismiss}>
          {outcome.retryable ? 'Try again' : 'Close'}
        </Button>
      </div>
    </div>
  )
}

function ruleCopy(rule: RefusedRule): string {
  return REFUSED_RULE_COPY[rule]
}

/**
 * Every second opinion this device has been given (D-094 §5).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  IT SITS LAST, AND THAT IS THE SAME MECHANISM AS THE PANEL ABOVE IT
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  Sola's decision panel, then the offer, then the archive. A history rendered
 *  above the current answer would put the reviewer's earlier words in front of
 *  Sola's present ones, which is the ordering D-008 exists to fix. The browser
 *  test asserts the document order.
 *
 *  **Collapsed by default and never a badge or a count on the tab bar.** A run of
 *  disagreements is exactly the kind of thing that becomes a score if it is put
 *  somewhere it can be totalled.
 *
 *  **There is no per-record delete.** Removing the one disagreement in a run of
 *  agreements would make the history a curated argument rather than a record;
 *  deletion is all-or-nothing and lives in Settings as its own category.
 */
function EarlierOpinions() {
  const db = useDatabase()
  const { state } = useAsyncData(async () => listAiProposals(db), [STORE.aiProposals])

  if (state.status !== 'ready' || !state.value.ok) return null

  const proposals = state.value.value
  if (proposals.length === 0) return null

  return (
    <details class={styles.history}>
      <summary class={styles.historySummary}>Earlier second opinions ({proposals.length})</summary>
      <ul class={styles.historyList}>
        {proposals.map((proposal) => (
          <li key={proposal.id} class={styles.historyRow}>
            <p class={styles.historyMeta}>
              <span class={styles.historyDate}>
                {formatCalendarDate(calendarDateFrom(new Date(proposal.requestedAt)), {
                  day: 'numeric',
                  month: 'short',
                })}
              </span>{' '}
              <span class={styles.historyVerdict}>{VERDICT_COPY[proposal.verdict]}</span>
            </p>
            <p class={styles.historyRationale}>{proposal.rationale}</p>
            {/* What produced it, so "why was last month's advice different?" is
                answerable rather than a shrug (D-094 §4). */}
            <p class={styles.historyProvenance}>
              {proposal.model} · {proposal.promptVersion}
              {proposal.disclosureProfile === 'withMeasurements'
                ? ' · measurements were included'
                : ''}
            </p>
          </li>
        ))}
      </ul>
    </details>
  )
}

/**
 * The proposal as it is read, immediately after it arrives.
 *
 * **It is stored on the way here, not from here** — see `send`. Writing at the
 * moment of display would tie the record to the user still being on the screen,
 * and losing a proposal by navigating away is exactly what phase 4 exists to
 * stop.
 */
function Proposal({
  proposal,
  storedId,
  onDismiss,
}: {
  proposal: GeminiProposal
  storedId: string | undefined
  onDismiss: () => void
}) {
  const [, navigate] = useLocation()

  /* Offered only when there is something to pre-fill AND a record to re-read it
     from. `SUGGESTION_KINDS` is `weeklyCommitment` alone and never grows without
     a decision — the phase type, the target rate and the route are not
     pre-fillable and never become so (D-062, D-093 §6). */
  const canPrefill = storedId !== undefined && proposal.suggestions.length > 0

  return (
    <div class={styles.proposal}>
      <p class={styles.verdict}>{VERDICT_COPY[proposal.verdict]}</p>
      <p class={styles.rationale}>{proposal.rationale}</p>
      <p class={styles.provenance}>
        Reviewed by {proposal.model}. This is a comment on your plan, not a change to it.
      </p>
      <div class={styles.actions}>
        {canPrefill ? (
          /* Opens My Plan's editor with the numbers filled in and writes nothing
             (D-093 §6). The label says "review" rather than "apply" because that
             is what the next screen actually asks for — a button promising to
             apply something, followed by a form, reads as a failed action. */
          <Button onClick={() => navigate(planFromProposalPath(storedId))}>
            Review this in My Plan
          </Button>
        ) : null}
        <Button variant="secondary" onClick={onDismiss}>
          Close
        </Button>
      </div>
    </div>
  )
}

/**
 * The verdict in Sola's voice rather than the model's.
 *
 * A closed vocabulary (D-094), so the phrasing is fixed here and the reviewer
 * cannot choose how its own agreement is characterised.
 */
const VERDICT_COPY = {
  agrees: 'Agrees with Project Sola’s read of this week.',
  partlyAgrees: 'Partly agrees with Project Sola’s read of this week.',
  disagrees: 'Disagrees with Project Sola’s read of this week.',
} as const
